/**
 * Frontend mirror of the backend's `sanitizeFileName` (lambda/shared/s3-upload.ts), used
 * ONLY by the duplicate-target collision guard in useUpload.ts.
 *
 * The backend derives each file's `s3_key` from `sanitizeFileName(fileName)`, so two files
 * whose names differ only in characters that sanitization strips — path separators, control
 * chars, `..`, leading/trailing dots+spaces, or content past 255 chars — collide on the SAME
 * s3_key. The guard must treat such files as one target; keying on the raw `file.name` misses
 * them and lets two concurrent batches race onto a single key (silent overwrite).
 *
 * Keep in sync with the backend. Erring toward MORE collapsing is safe (the guard just falls
 * back to per-folder ordering); under-collapsing is the bug, so this must stay at least as
 * aggressive as the backend.
 */
export function sanitizeFileNameForCollision(fileName: string, maxLength = 255): string {
  if (!fileName) return '';

  // Remove path components (/, \)
  let sanitized = fileName.replace(/[/\\]/g, '');
  // Remove null bytes and control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
  // Remove path traversal attempts
  sanitized = sanitized.replace(/\.\./g, '');
  // Remove leading/trailing dots and spaces
  sanitized = sanitized.trim().replace(/^\.+/, '');

  // Truncate while preserving extension (matches backend so >255-char names collide the same way)
  if (sanitized.length > maxLength) {
    const extension = sanitized.match(/\.[^.]+$/)?.[0] || '';
    const nameWithoutExt = sanitized.slice(0, sanitized.length - extension.length);
    sanitized = nameWithoutExt.slice(0, maxLength - extension.length) + extension;
  }

  return sanitized;
}
